import React, { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import {
  FilterTogglesModel,
  FilterTogglesOptionModel,
  FilterTypes,
  getCoursesPath,
  getFilterOptionValueSelector,
  getFilters,
  pushDataLayer,
  useFastLinksContext,
} from '@lerna-core';
import { FilterToggleContainerProps } from './filterToggles.model';
import {
  FilterTogglesContainerStyled,
  FilterToggleWrapperStyled,
  ToggleStyled,
} from './filterToggles.styled';
import { FilterEvent, getMainFilterEventData } from '../filters.analytics';
import { ToggleContainer } from '@features/common/toggle';
import { selectFilterTogglesOption } from '../filters.helper';
import {
  TooltipDesktopContainer,
  TooltipMobileContainer,
} from '@features/common/filters';

const FilterTogglesOptionContainer = ({
  filter,
  option,
  analyticsType,
  isMobile,
}: {
  isMobile: boolean;
  analyticsType?: keyof typeof FilterTypes;
  filter: FilterTogglesModel;
  option: FilterTogglesOptionModel;
}) => {
  const router = useRouter();
  const fastLinks = useFastLinksContext();
  const filters = useSelector(getFilters);
  const coursesPath = getCoursesPath(router.query);

  const isActive = useSelector(
    getFilterOptionValueSelector(filter.slug, option.slug)
  );

  const handleClick = (option: FilterTogglesOptionModel): void => {
    selectFilterTogglesOption(
      router,
      isActive,
      filter,
      option,
      filters,
      fastLinks,
      coursesPath
    );

    if (analyticsType === FilterTypes.main) {
      pushDataLayer(
        getMainFilterEventData(
          FilterEvent.constructorFiltersMain,
          filter,
          undefined,
          false
        )
      );
    }
  };

  return (
    <FilterToggleWrapperStyled>
      {option.value}
      {!!(option.tooltip && !isMobile) && (
        <TooltipDesktopContainer content={option.tooltip} />
      )}
      {!!(option.tooltip && isMobile) && (
        <TooltipMobileContainer content={option.tooltip} />
      )}
      <ToggleStyled>
        <ToggleContainer
          isActive={isActive}
          onClick={handleClick.bind(null, option)}
        />
      </ToggleStyled>
    </FilterToggleWrapperStyled>
  );
};

export const FilterTogglesContainer = ({
  filter,
  isMobile = false,
  analyticsType = FilterTypes.main,
}: FilterToggleContainerProps): ReactElement => {
  return (
    <FilterTogglesContainerStyled isMobile={isMobile}>
      {filter.options
        .filter((option) => option.isVisible)
        .map((option) => (
          <FilterTogglesOptionContainer
            key={option.slug}
            filter={filter}
            option={option}
            analyticsType={analyticsType}
            isMobile={isMobile}
          />
        ))}
    </FilterTogglesContainerStyled>
  );
};
